import React, { useEffect, useMemo, useState, Children, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ExternalLinkIcon, LogOutIcon } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, getDoc, deleteDoc, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
// ── Firebase Initialization ─────────────────────────────────────
const firebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:611f17b3063fd1c86c8ef3',
  measurementId: 'G-LBQC232NCB'
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const creatorFirebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:9d703a73017f16676c8ef3',
  measurementId: 'G-LYZ8QPBFTZ'
};
const creatorApp = initializeApp(creatorFirebaseConfig, 'creator');
const creatorAuth = getAuth(creatorApp);
const creatorDb = getFirestore(creatorApp);
// ── Sparkle Overlay ─────────────────────────────────────────────
function ActionButtons({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.8,
    duration: 0.5
  }} className="mt-16 mb-10 flex flex-col items-center justify-center w-full" data-id="element-0">
      <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1">
        <motion.a href="mailto:gsfliveagency@gmail.com" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all" data-id="element-2">
          Contact Us
        </motion.a>
        {currentPage !== 'contact' && <motion.button onClick={() => onNavigate('contact')} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur-sm text-white font-semibold font-['Montserrat'] hover:bg-white/10 transition-all" data-id="element-3">
            Join GSF
          </motion.button>}
      </div>
    </motion.div>;
}
function SparkleOverlay() {
  const glitter = useMemo(() => {
    const items: {
      id: number;
      x: number;
      y: number;
      size: number;
      delay: number;
      duration: number;
      type: 'bright' | 'medium' | 'soft';
      color: string;
    }[] = [];
    const colors = ['rgba(200,130,255,1)', 'rgba(255,130,200,1)', 'rgba(180,100,255,1)', 'rgba(255,150,220,1)', 'rgba(160,120,255,1)'];
    for (let i = 0; i < 150; i++) {
      const type = i < 30 ? 'bright' : i < 80 ? 'medium' : 'soft';
      items.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: type === 'bright' ? Math.random() * 2.5 + 1.5 : type === 'medium' ? Math.random() * 2 + 1 : Math.random() * 1.5 + 0.5,
        delay: Math.random() * 6,
        duration: type === 'bright' ? Math.random() * 1.5 + 1 : Math.random() * 2.5 + 1.5,
        type,
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }
    return items;
  }, []);
  return <div className="fixed inset-0 pointer-events-none z-[5]" data-id="element-4">
      {glitter.map(g => <div key={g.id} className="glitter-particle" data-type={g.type} style={{
      left: `${g.x}%`,
      top: `${g.y}%`,
      width: `${g.size}px`,
      height: `${g.size}px`,
      animationDelay: `${g.delay}s`,
      animationDuration: `${g.duration}s`,
      '--glitter-color': g.color
    } as React.CSSProperties} data-id="element-5" />)}
      {/* Light wave sweep */}
      <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '30%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(168,130,255,0.04) 30%, rgba(255,255,255,0.06) 50%, rgba(130,180,255,0.04) 70%, transparent 100%)',
      animation: 'light-wave 12s ease-in-out infinite'
    }} data-id="element-6" />
    </div>;
}
type PageId = 'home' | 'team' | 'about' | 'why' | 'services' | 'contact' | 'agent-login' | 'creator-login' | 'portal-hub' | 'creator-hub' | 'portal' | 'portal-qvc' | 'creator-portal' | 'creator-portal-qvc';
const NAV_ITEMS: {
  label: string;
  page: PageId;
}[] = [{
  label: 'Home',
  page: 'home'
}, {
  label: 'About Us',
  page: 'about'
}, {
  label: 'Meet the Team',
  page: 'team'
}, {
  label: 'Why GSF',
  page: 'why'
}, {
  label: 'Services',
  page: 'services'
}, {
  label: 'Join',
  page: 'contact'
}];
// ── Org Chart Data ──────────────────────────
interface OrgNode {
  id: string;
  name: string;
  title: string;
  photo?: string;
  tiktok?: string;
  size: 'owner' | 'director' | 'agent' | 'creator';
  children?: OrgNode[];
}
const ORG_DATA: OrgNode = {
  id: 'owner',
  name: 'STARPLAYER757',
  title: 'GSF Owner / Founder',
  photo: 'https://cdn.magicpatterns.com/uploads/1iGaPStP5rkHhAVcvoNPYL/file_00000000aa0c71fb8c106d24270950d5.png',
  tiktok: 'https://www.tiktok.com/@zoeaking?_r=1&_t=ZP-95d2CTbttLr',
  size: 'owner',
  children: [{
    id: 'dir-1',
    name: 'BaddieDaBoss',
    title: 'Director of Coordination',
    photo: 'https://cdn.magicpatterns.com/uploads/sDv2sLdkr4YwNawiY2kTZX/file_0000000049d071f7b54df7f1981976fd.png',
    tiktok: 'https://www.tiktok.com/@nfskmx26?_r=1&_t=ZP-95d239RoLCZ',
    size: 'director',
    children: [{
      id: 'agt-1',
      name: 'BaddieDaBoss',
      title: 'Lead Agent',
      photo: 'https://cdn.magicpatterns.com/uploads/sDv2sLdkr4YwNawiY2kTZX/file_0000000049d071f7b54df7f1981976fd.png',
      tiktok: 'https://www.tiktok.com/@nfskmx26?_r=1&_t=ZP-95d239RoLCZ',
      size: 'agent',
      children: [{
        id: 'crt-1',
        name: 'NESSY™',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/hLFsnjZiAts7jJPbQuBKjh/file_00000000bb3c71f5bc462fa45847f7da.png',
        tiktok: 'https://www.tiktok.com/@nessytherilla',
        size: 'creator'
      }, {
        id: 'crt-2',
        name: 'bigdrikababii',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/1hsZiLDsbioxc7n7mtRhU3/file_00000000af0871f7ab92d245a9d4b873.png',
        tiktok: 'https://www.tiktok.com/@bigdrikababii?_r=1&_t=ZP-95d23vNAzUc',
        size: 'creator'
      }]
    }, {
      id: 'agt-2',
      name: 'Trippin.with.mandy',
      title: 'GSF Agent',
      photo: 'https://cdn.magicpatterns.com/uploads/w3UjyMZg6n8Z4PKxA8tdxC/file_000000008e7871f7a76604bb1ea27fa8.png',
      tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
      size: 'agent',
      children: [{
        id: 'crt-3',
        name: 'ZettaMINDset',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/mdkJpcrJ8rTabmafAvUDge/file_0000000072d871fb97d632a224ca5e0b.png',
        tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
        size: 'creator'
      }]
    }, {
      id: 'agt-3',
      name: 'Shorty713',
      title: 'GSF Agent',
      photo: 'https://cdn.magicpatterns.com/uploads/adX7uJpp4Hv7ArTeTxQRSR/file_00000000b78471f78010cbaf189ed5aa.png',
      tiktok: 'https://www.tiktok.com/@codmshorty?_r=1&_t=ZP-95d1z9kFVq2',
      size: 'agent',
      children: [{
        id: 'crt-4',
        name: 'KING JAY23',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/gsR8o9vzc1477WsQzZo8t1/file_00000000cdc071f58399905eaed5754a.png',
        tiktok: 'https://www.tiktok.com/@jay232331?_r=1&_t=ZP-95eKXz0IgUZ',
        size: 'creator'
      }]
    }, {
      id: 'agt-4',
      name: 'J3',
      title: 'GSF Agent',
      photo: 'https://cdn.magicpatterns.com/uploads/bKGX35nhi87cBboDJ8DUkh/file_0000000034d071fd89faaff830ddebb2.png',
      tiktok: 'https://www.tiktok.com/@chyyy..0110?_r=1&_t=ZP-95d27IRtmj1',
      size: 'agent',
      children: [{
        id: 'crt-5',
        name: 'IAM_JDOLLA',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/qnMzegtyNzJ76tDvU1ZvZz/file_00000000fb2c720c926f898f076c1222.png',
        tiktok: 'https://www.tiktok.com/@jdolla_74?_r=1&_t=ZP-95d1zT2i8L9',
        size: 'creator'
      }, {
        id: 'crt-6',
        name: 'Prince justice 355GSF',
        title: 'Live Creator',
        photo: 'https://cdn.magicpatterns.com/uploads/4DV9MNKESWuaA8Mgr4q6dN/file_0000000054ec71f58e3e0b7ad24a447d.png',
        tiktok: 'https://www.tiktok.com/@princejustice335',
        size: 'creator'
      }]
    }]
  }, {
    id: 'dir-2',
    name: 'ZettaMINDset',
    title: 'Director of GSF / QVC',
    photo: 'https://cdn.magicpatterns.com/uploads/exHyQEonQD5samLgBx1T8v/file_000000002c6871fbb2bafc86da8f7c19.png',
    tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
    size: 'director',
    children: [{
      id: 'agt-5',
      name: 'Trippin.with.mandy',
      title: 'GSF / QVC Advisory Agent',
      photo: 'https://cdn.magicpatterns.com/uploads/2HdfMTeHJjDfxQGqiVZv4f/file_00000000e00071f78578a71f0cdf321a.png',
      tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
      size: 'agent'
    }, {
      id: 'agt-6',
      name: 'bigdrikababii',
      title: 'Live Creator',
      photo: 'https://cdn.magicpatterns.com/uploads/1hsZiLDsbioxc7n7mtRhU3/file_00000000af0871f7ab92d245a9d4b873.png',
      tiktok: 'https://www.tiktok.com/@bigdrikababii?_r=1&_t=ZP-95d23vNAzUc',
      size: 'creator'
    }]
  }]
};
// ── Org Chart Components ───────────────────────────────────────────
function getInitials(name: string) {
  const clean = name.replace(/[^a-zA-Z0-9 ]/g, ' ').trim();
  const parts = clean.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.substring(0, 2).toUpperCase();
}
function OrgCard({
  node
}: {
  node: OrgNode;
}) {
  const imageSizes = {
    owner: 'w-44 md:w-72',
    director: 'w-36 md:w-60',
    agent: 'w-32 md:w-52',
    creator: 'w-28 md:w-44'
  };
  return <motion.a href={node.tiktok || '#'} target={node.tiktok ? '_blank' : '_self'} rel={node.tiktok ? 'noopener noreferrer' : ''} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} whileHover={{
    scale: 1.08,
    y: -5
  }} className={`flex flex-col items-center cursor-pointer group ${imageSizes[node.size]}`} data-id="element-7">
      {node.photo ? <img src={node.photo} alt={node.name} className="object-contain group-hover(drop-shadow(0 0 15px rgba(168,85,247,0.3)))" data-id="element-8" /> : <div className="w-full aspect-square bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col items-center justify-center p-3 rounded-full" data-id="element-9">
          <span className="text-white font-bold text-lg md:text-2xl tracking-wider" data-id="element-10">
            {getInitials(node.name)}
          </span>
          <span className="text-white/80 text-[8px] md:text-xs mt-1 text-center font-medium" data-id="element-11">
            {node.name}
          </span>
          <span className="text-white/50 text-[7px] md:text-[10px] text-center" data-id="element-12">
            {node.title}
          </span>
        </div>}
    </motion.a>;
}
function TreeNode({
  node,
  isRoot = false,
  isFirst = false,
  isLast = false,
  isOnly = false
}: {
  node: OrgNode;
  isRoot?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isOnly?: boolean;
}) {
  return <div className="flex flex-col items-center relative px-1 md:px-3" data-id="element-13">
      {/* Lines above the node (skip for root) */}
      {!isRoot && <>
          {/* Horizontal line connecting siblings */}
          {!isOnly && <div className={`absolute top-0 h-[2px] bg-gradient-to-r from-purple-500/60 to-blue-500/60
              ${isFirst ? 'left-1/2 right-0' : isLast ? 'left-0 right-1/2' : 'left-0 right-0'}
            `} data-id="element-14" />}
          {/* Vertical line down to card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-15" />
        </>}

      {/* The Card */}
      <OrgCard node={node} data-id="element-16" />

      {/* Lines below the node (if has children) */}
      {node.children && node.children.length > 0 && <>
          {/* Vertical line down from card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-17" />
          {/* Children container */}
          <div className="flex justify-center items-start relative" data-id="element-18">
            {node.children.map((child, idx) => <TreeNode key={child.id} node={child} isRoot={false} isFirst={idx === 0} isLast={idx === node.children.length - 1} isOnly={node.children.length === 1} data-id="element-19" />)}
          </div>
        </>}
    </div>;
}
// ── Meet the Team Page ─────────────────────────────────────────
function MeetTheTeam() {
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-20">
      {/* Section Title */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-21">
        <h2 className="font-['Montserrat'] font-black text-3xl md:text-5xl tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-purple-300 uppercase" data-id="element-22">
          GSF Agency Chain of Command
        </h2>
        <p className="text-white/50 mt-3 text-sm md:text-base max-w-md mx-auto" data-id="element-23">
          Tap a card to visit their TikTok profile.
        </p>
      </motion.div>

      {/* Highlight Reel */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.3
    }} className="text-center mb-16" data-id="element-24">
        <h3 className="font-['Montserrat'] font-black text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-25">
          🎬 Highlight Reel
        </h3>
        <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto" data-id="element-26">
          {['https://cdn.magicpatterns.com/uploads/4ZCN2hYEVSPiTDGnkbCFuf/8e4b4ec9eaf3df3cb2d789f280e7966e.mp4', 'https://cdn.magicpatterns.com/uploads/jFjRAqAPAQzQ6y1RnVr6pL/77ed4a9232548fb2569a2d5f0cdbd790.mp4'].map((url, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5 + i * 0.15,
          duration: 0.4
        }} className="w-48 md:w-64 rounded-2xl overflow-hidden backdrop-blur-md bg-black/40 border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-27">
              <video src={url} controls className="w-full h-auto aspect-[9/16] object-cover" preload="metadata" data-id="element-28" />
            </motion.div>)}
        </div>
      </motion.div>

      {/* Org Chart / Family Tree */}
      <div className="w-full overflow-x-auto pb-10 scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-transparent" data-id="element-29">
        <div className="min-w-max mx-auto flex justify-center px-4" data-id="element-30">
          <TreeNode node={ORG_DATA} isRoot={true} data-id="element-31" />
        </div>
      </div>
    </div>;
}
// ── Printables Page ────────────────────────────────────────────
// ── Navbar ──────────────────────────────────────────────────────
function Navbar({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4" data-id="element-32">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-md border-b border-white/10" data-id="element-33" />

      <div className="relative max-w-7xl mx-auto flex items-center justify-between" data-id="element-34">
        {/* Logo */}
        <button onClick={() => onNavigate('home')} className="flex items-center gap-2 group" data-id="element-35">
          <span className="font-['Montserrat'] font-black text-2xl tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-300 group-hover:to-blue-300 transition-all duration-300" data-id="element-36">
            GSF
          </span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8" data-id="element-37">
          {NAV_ITEMS.map(item => <button key={item.label} onClick={() => onNavigate(item.page)} className={`relative font-['Inter'] text-sm font-medium transition-colors py-2 group ${currentPage === item.page ? 'text-white' : 'text-white/80 hover:text-white'}`} data-id="element-38">
              {item.label}
              <span className={`absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-purple-500 to-blue-500 transition-transform duration-300 origin-left shadow-[0_0_8px_rgba(168,85,247,0.6)] ${currentPage === item.page ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-39" />
            </button>)}
          <div className="h-6 w-[1px] bg-white/20 mx-2" data-id="element-40" />
          <div className="flex items-center gap-4" data-id="element-41">
            <button onClick={() => onNavigate('agent-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-42">
              Agent Login
            </button>
            <button onClick={() => onNavigate('creator-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-43">
              Creator Login
            </button>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden relative z-50 p-2 text-white hover:text-white transition-colors" data-id="element-44">
          {isOpen ? <XIcon size={24} data-id="element-45" /> : <MenuIcon size={24} data-id="element-46" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-47">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 right-0 bg-black/80 backdrop-blur-xl border-b border-white/10 md:hidden" data-id="element-48">
            <div className="flex flex-col px-6 py-4 gap-4" data-id="element-49">
              {NAV_ITEMS.map(item => <button key={item.label} onClick={() => {
            onNavigate(item.page);
            setIsOpen(false);
          }} className={`text-left font-['Inter'] text-lg font-medium hover:pl-2 transition-all duration-300 border-l-2 ${currentPage === item.page ? 'text-white border-purple-500 pl-2' : 'text-white hover:text-white'}`} data-id="element-50">
                  {item.label}
                </button>)}
              <div className="w-full h-[1px] bg-white/10 my-2" data-id="element-51" />
              <button onClick={() => {
            onNavigate('agent-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-52">
                Agent Login
              </button>
              <button onClick={() => {
            onNavigate('creator-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-53">
                Creator Login
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
// ── Login Page ──────────────────────────────────────────────────
function LoginPage({
  title,
  type,
  onLogin
}: {
  title: string;
  type: 'agent' | 'creator';
  onLogin: (displayName: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      if (type === 'agent') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(creatorAuth, email, password);
      }
      // Try to fetch display name from Firestore "users" collection
      let displayName = email.split('@')[0];
      try {
        // Try exact email as doc ID first, then lowercase
        let userDocSnap = await getDoc(doc(db, 'users', email));
        if (!userDocSnap.exists()) {
          userDocSnap = await getDoc(doc(db, 'users', email.toLowerCase()));
        }
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          // Support both "Name" and "name" field
          displayName = userData?.Name || userData?.name || displayName;
        }
      } catch (e) {
        // If Firestore lookup fails, just use email prefix
      }
      onLogin(displayName);
    } catch (err: any) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-20" data-id="element-54">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="max-w-md backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-8 shadow-[0 0 40px rgba(168,85,247,0.15)]" data-id="element-55">
        <div className="text-center mb-8" data-id="element-56">
          <h2 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300 mb-2" data-id="element-57">
            {title}
          </h2>
          <p className="text-white/50 text-sm font-['Montserrat']" data-id="element-58">
            Enter your credentials to access the portal
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-59">
          {error && <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-sm text-center" data-id="element-60">
              {error}
            </div>}
          <div data-id="element-61">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-62">
              Email
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-'Inter'" placeholder="Enter your email" required data-id="element-63" />
          </div>
          <div data-id="element-64">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-65">
              Password
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-'Inter'" placeholder="••••••••" required data-id="element-66" />
          </div>
          <button type="submit" disabled={loading} className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-base font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-67">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </motion.div>
    </div>;
}
// ── Internal Portal Page ────────────────────────────────────────
interface PostLike {
  email: string;
  name: string;
}
interface Post {
  id: string;
  content: string;
  imageUrl?: string;
  authorName: string;
  authorInitials: string;
  createdAt: Timestamp | null;
  sharedToCreators?: boolean;
  postedBy?: 'agent' | 'creator';
  crossPosted?: boolean;
  fontColor?: string;
  fontSize?: string;
  likes?: PostLike[];
  comments: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }[];
}
function PortalPage({
  user,
  displayName,
  title,
  subtitle,
  collectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  title: string;
  subtitle: string;
  collectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const [shareToCreators, setShareToCreators] = useState(false);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState('base');
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        postsData.push({
          id: doc.id,
          ...doc.data()
        } as Post);
      });
      setPosts(postsData);
    });
    return () => unsubscribe();
  }, []);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'agent',
        crossPosted: shareToCreators,
        fontColor,
        fontSize
      });
      setNewPostContent('');
      setShareToCreators(false);
      setFontColor('#ffffff');
      setFontSize('base');
    } catch (error: any) {
      console.error('Error adding post:', error);
      alert('Failed to post: ' + (error?.message || 'Unknown error.'));
    } finally {
      setIsPosting(false);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Agent';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleDeletePost = async (postId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteDoc(doc(db, collectionName, postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-68">
      <div className="max-w-3xl mx-auto" data-id="element-69">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-70">
          <div data-id="element-71">
            <div className="flex items-center gap-4 mb-2" data-id="element-72">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-73">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-74">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-75">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-76">
              <LogOutIcon size={16} data-id="element-77" />
              Sign Out
            </button>}
        </motion.div>

        {/* Create Post Box */}
        {user && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-8" data-id="element-78">
            <textarea value={newPostContent} onChange={e => setNewPostContent(e.target.value)} style={{
          color: fontColor
        }} className={`w-full bg-black/20 border border-white/5 rounded-xl p-4 placeholder-white/40 focus:outline-none focus:border-purple-500/50 resize-none font-['Inter'] ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : fontSize === 'xl' ? 'text-xl' : fontSize === '2xl' ? 'text-2xl' : 'text-base'}`} rows={3} placeholder="Share an update with the team..." data-id="element-79" />
            <div className="flex flex-wrap items-center gap-3 mt-4" data-id="element-80">
              {/* Font Color */}
              <div className="flex items-center gap-2" data-id="element-81">
                <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-82">
                  Color:
                </span>
                <div className="flex gap-1.5" data-id="element-83">
                  {[{
                color: '#ffffff',
                label: 'White'
              }, {
                color: '#c084fc',
                label: 'Purple'
              }, {
                color: '#22d3ee',
                label: 'Cyan'
              }, {
                color: '#f472b6',
                label: 'Pink'
              }, {
                color: '#facc15',
                label: 'Yellow'
              }, {
                color: '#4ade80',
                label: 'Green'
              }, {
                color: '#f87171',
                label: 'Red'
              }].map(c => <button key={c.color} type="button" onClick={() => setFontColor(c.color)} title={c.label} className={`w-6 h-6 rounded-full border-2 transition-all ${fontColor === c.color ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`} style={{
                backgroundColor: c.color
              }} data-id="element-84" />)}
                </div>
              </div>
              {/* Font Size */}
              <div className="flex items-center gap-2" data-id="element-85">
                <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-86">
                  Size:
                </span>
                <div className="flex gap-1" data-id="element-87">
                  {[{
                value: 'sm',
                label: 'S'
              }, {
                value: 'base',
                label: 'M'
              }, {
                value: 'lg',
                label: 'L'
              }, {
                value: 'xl',
                label: 'XL'
              }, {
                value: '2xl',
                label: '2XL'
              }].map(s => <button key={s.value} type="button" onClick={() => setFontSize(s.value)} className={`px-2 py-0.5 rounded text-xs font-['Montserrat'] font-bold transition-all ${fontSize === s.value ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-88">
                      {s.label}
                    </button>)}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3" data-id="element-89">
              <button type="button" onClick={() => setShareToCreators(!shareToCreators)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all font-['Montserrat'] text-sm ${shareToCreators ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-90">
                <span className="text-base" data-id="element-91">
                  {shareToCreators ? '✅' : '⬜'}
                </span>
                Cross-post to Creators
              </button>
              <button onClick={handlePost} disabled={isPosting || !newPostContent.trim()} className="rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white font-semibold text-sm" data-id="element-92">
                {isPosting ? 'Posting...' : 'Post Update'}
              </button>
            </div>
          </motion.div>}

        {!user && <div className="mb-8 p-4 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-200 text-center font-['Montserrat']" data-id="element-93">
            You are viewing the portal in read-only mode. Please login as an
            Agent to post and comment.
          </div>}

        {/* Feed */}
        <div className="space-y-6" data-id="element-94">
          {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-95">
              No posts yet. Be the first to share an update!
            </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + idx * 0.1
        }} className="backdrop-blur-md bg-black/40 border border-white/5 rounded-2xl p-5 md:p-6" data-id="element-96">
                <div className="flex items-center gap-3 mb-4" data-id="element-97">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-98">
                    {post.authorInitials}
                  </div>
                  <div className="flex-1" data-id="element-99">
                    <div className="flex items-center gap-2" data-id="element-100">
                      <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-101">
                        {post.authorName}
                      </h4>
                      {post.crossPosted && <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-semibold font-['Montserrat']" data-id="element-102">
                          Shared to Creators
                        </span>}
                    </div>
                    <p className="text-white/40 text-xs" data-id="element-103">
                      {post.createdAt ? new Date(post.createdAt.toDate()).toLocaleString() : 'Just now'}
                    </p>
                  </div>
                  {user && <button onClick={() => handleDeletePost(post.id)} className="text-white/20 hover:text-red-400 transition-colors text-sm font-['Montserrat'] p-1" title="Delete post" data-id="element-104">
                      🗑️
                    </button>}
                </div>

                {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
            color: post.fontColor || 'rgba(255,255,255,0.8)'
          }} data-id="element-105">
                    {post.content}
                  </p>}

                {/* Like Button */}
                <div className="mb-4 flex items-center gap-3" data-id="element-106">
                  <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-107">
                    <span className="text-lg" data-id="element-108">
                      {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                    </span>
                    <span data-id="element-109">{post.likes?.length || 0}</span>
                  </button>
                  {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-110">
                      Liked by {post.likes?.map(l => l.name).join(', ')}
                    </p>}
                </div>

                {/* Comments Section */}
                <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-111">
                  {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-112">
                      <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-113">
                        {comment.authorInitials}
                      </div>
                      <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-114">
                        <div className="flex justify-between items-baseline mb-1" data-id="element-115">
                          <h5 className="text-white/90 text-sm font-semibold" data-id="element-116">
                            {comment.authorName}
                          </h5>
                          <span className="text-white/30 text-[10px]" data-id="element-117">
                            {comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-118">
                          {comment.content}
                        </p>
                      </div>
                    </div>)}

                  {user && <div className="flex gap-3 items-center mt-4" data-id="element-119">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-120">
                        {user.email?.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 flex gap-2" data-id="element-121">
                        <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                  ...prev,
                  [post.id]: e.target.value
                }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-122" />
                        <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-purple-400 hover:text-purple-300" data-id="element-123">
                          Post
                        </button>
                      </div>
                    </div>}
                </div>
              </motion.div>)}
        </div>
      </div>
    </div>;
}
// ── Creator Portal Page ─────────────────────────────────────────
const APPROVED_CREATOR_EMAILS = ['mandyleec12@gmail.com', 'test@gsf.com', 'creator@gsf.com'];
function CreatorPortalPage({
  user,
  displayName,
  title,
  subtitle,
  collectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  title: string;
  subtitle: string;
  collectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const isApprovedCreator = user?.email ? APPROVED_CREATOR_EMAILS.includes(user.email) : false;
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        postsData.push({
          id: doc.id,
          ...data
        } as Post);
      });
      // Only show posts that were cross-posted by agents, or posted by creators
      const filtered = postsData.filter(p => p.postedBy === 'creator' || p.crossPosted === true);
      setPosts(filtered);
    });
  }, [collectionName]);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user || !isApprovedCreator) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'creator'
      });
      setNewPostContent('');
    } catch (error) {
      console.error('Error adding post:', error);
    } finally {
      setIsPosting(false);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Creator';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-124">
      <div className="max-w-3xl mx-auto" data-id="element-125">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-126">
          <div data-id="element-127">
            <div className="flex items-center gap-4 mb-2" data-id="element-128">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-129">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-130">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-131">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-132">
              <LogOutIcon size={16} data-id="element-133" />
              Sign Out
            </button>}
        </motion.div>

        {!user && <div className="mb-8 p-4 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-200 text-center font-['Montserrat']" data-id="element-134">
            You are viewing the dashboard in read-only mode. Please login to
            like and comment.
          </div>}

        {/* Feed */}
        <div className="space-y-6" data-id="element-135">
          {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-136">
              No posts yet.
            </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + idx * 0.1
        }} className={`backdrop-blur-md rounded-2xl p-5 md:p-6 ${post.postedBy === 'agent' ? 'shadow-[0 0 15px rgba(168,85,247,0.15)]' : 'bg-black/40 border border-white/5'}`} data-id="element-137">
                <div className="flex items-center gap-3 mb-4" data-id="element-138">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${post.postedBy === 'agent' ? 'bg-gradient-to-br from-purple-500 to-blue-500' : 'bg-gradient-to-br from-cyan-500 to-blue-500'}`} data-id="element-139">
                    {post.authorInitials}
                  </div>
                  <div data-id="element-140">
                    <div className="flex items-center gap-2" data-id="element-141">
                      <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-142">
                        {post.authorName}
                      </h4>
                      {post.postedBy === 'agent' && <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-semibold font-['Montserrat']" data-id="element-143">
                          Agent
                        </span>}
                    </div>
                    <p className="text-white/40 text-xs" data-id="element-144">
                      {post.createdAt ? new Date(post.createdAt.toDate()).toLocaleString() : 'Just now'}
                    </p>
                  </div>
                </div>
                {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
            color: post.fontColor || 'rgba(255,255,255,0.8)'
          }} data-id="element-145">
                    {post.content}
                  </p>}

                {/* Like Button */}
                <div className="mb-4 flex items-center gap-3" data-id="element-146">
                  <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-147">
                    <span className="text-lg" data-id="element-148">
                      {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                    </span>
                    <span data-id="element-149">{post.likes?.length || 0}</span>
                  </button>
                  {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-150">
                      Liked by {post.likes?.map(l => l.name).join(', ')}
                    </p>}
                </div>

                {/* Comments Section */}
                <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-151">
                  {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-152">
                      <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-153">
                        {comment.authorInitials}
                      </div>
                      <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-154">
                        <div className="flex justify-between items-baseline mb-1" data-id="element-155">
                          <h5 className="text-white/90 text-sm font-semibold" data-id="element-156">
                            {comment.authorName}
                          </h5>
                          <span className="text-white/30 text-[10px]" data-id="element-157">
                            {comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-158">
                          {comment.content}
                        </p>
                      </div>
                    </div>)}

                  {user && <div className="flex gap-3 items-center mt-4" data-id="element-159">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-160">
                        {user.email?.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 flex gap-2" data-id="element-161">
                        <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                  ...prev,
                  [post.id]: e.target.value
                }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-162" />
                        <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-cyan-400 hover:text-cyan-300" data-id="element-163">
                          Post
                        </button>
                      </div>
                    </div>}
                </div>
              </motion.div>)}
        </div>
      </div>
    </div>;
}
// ── Portal Hub ──────────────────────────────────────────────────
function PortalHub({
  title,
  subtitle,
  onNavigate,
  onSignOut
}: {
  title: string;
  subtitle: string;
  onNavigate: (page: 'gsf' | 'qvc') => void;
  onSignOut: () => void;
}) {
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8 flex items-center justify-center" data-id="element-164">
      <div className="max-w-4xl w-full" data-id="element-165">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12 relative" data-id="element-166">
          <button onClick={onSignOut} className="absolute top-0 right-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-167">
            <LogOutIcon size={16} data-id="element-168" />
            Sign Out
          </button>
          <h2 className="font-['Montserrat'] font-black text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-169">
            {title}
          </h2>
          <p className="text-white/50 font-['Montserrat'] text-lg" data-id="element-170">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-171">
          <motion.button initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.1
        }} onClick={() => onNavigate('gsf')} className="group relative backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 md:p-8 text-left transition-all hover:border-purple-500/30" data-id="element-172">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-600/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-173" />
            <h3 className="font-['Montserrat'] font-black text-3xl text-white mb-4" data-id="element-174">
              GSF Live
            </h3>
            <p className="text-white/60 font-['Montserrat'] leading-relaxed" data-id="element-175">
              Access the main GSF Agency chat board. Connect with the team,
              share updates, and stay in the loop with all things GSF.
            </p>
            <div className="mt-8 flex items-center text-purple-400 font-semibold font-['Montserrat'] group-hover:text-purple-300" data-id="element-176">
              Enter Chat{' '}
              <span className="ml-2 transition-transform group-hover:translate-x-2" data-id="element-177">
                →
              </span>
            </div>
          </motion.button>

          <motion.button initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} onClick={() => onNavigate('qvc')} className="group relative backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 md:p-8 text-left overflow-hidden transition-all hover:border-cyan-500/30" data-id="element-178">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-600/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-179" />
            <h3 className="font-['Montserrat'] font-black text-3xl text-white mb-4" data-id="element-180">
              GSF/QVC Live
            </h3>
            <p className="text-white/60 font-['Montserrat'] leading-relaxed" data-id="element-181">
              Access the dedicated GSF/QVC chat board. Focused entirely on the
              QVC selling side, product updates, and specific strategies.
            </p>
            <div className="mt-8 flex items-center text-cyan-400 font-semibold font-['Montserrat'] group-hover:text-cyan-300" data-id="element-182">
              Enter Chat{' '}
              <span className="ml-2 transition-transform group-hover:translate-x-2" data-id="element-183">
                →
              </span>
            </div>
          </motion.button>
        </div>
      </div>
    </div>;
}
// ── App ─────────────────────────────────────────────────────────
export function App() {
  const [currentPage, setCurrentPage] = useState<PageId>('home');
  const [user, setUser] = useState<User | null>(null);
  const [creatorUser, setCreatorUser] = useState<User | null>(null);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [creatorDisplayName, setCreatorDisplayName] = useState('');
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
    });
    const unsubscribeCreatorAuth = onAuthStateChanged(creatorAuth, currentUser => {
      setCreatorUser(currentUser);
    });
    return () => {
      unsubscribeAuth();
      unsubscribeCreatorAuth();
    };
  }, []);
  return <div className="relative w-full min-h-screen bg-black overflow-hidden font-['Inter']" data-id="element-184">
      <Navbar currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-185" />

      {/* Galaxy background — always visible */}
      <img src="https://cdn.magicpatterns.com/uploads/im5SGBMg4Y2MsphHgUksHv/file_000000008e0c722f98a57c1e17d4b26a.png" alt="GSF Agency - Galaxy lounge" className="fixed inset-0 w-full h-full object-cover" data-id="element-186" />
      <div className="fixed inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 pointer-events-none" data-id="element-187" />
      <SparkleOverlay data-id="element-188" />
      <motion.div animate={{
      opacity: [0.03, 0.08, 0.03]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="fixed inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 pointer-events-none" data-id="element-189" />

      {/* Page Content */}
      <AnimatePresence mode="wait" data-id="element-190">
        {currentPage === 'home' && <motion.div key="home" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} className="relative z-10 min-h-screen flex flex-col items-center justify-center pt-20" data-id="element-191">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="text-center" data-id="element-192">
              {/* GSF — thrown onto screen with overshoot bounce */}
              <motion.div initial={{
            scale: 3.5,
            opacity: 0,
            rotate: -15,
            y: -300,
            filter: 'blur(12px)'
          }} animate={{
            scale: [3.5, 0.9, 1.05, 1],
            opacity: [0, 1, 1, 1],
            rotate: [-15, 3, -1, 0],
            y: [-300, 10, -5, 0],
            filter: ['blur(12px)', 'blur(0px)', 'blur(0px)', 'blur(0px)']
          }} transition={{
            duration: 1,
            times: [0, 0.5, 0.75, 1],
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-193">
                {/* Continuous floating after landing */}
                <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.2
            }} data-id="element-194">
                  <motion.h1 className="font-['Montserrat'] font-black text-8xl md:text-[10rem] tracking-tighter text-transparent bg-clip-text text-glow-animated text-shimmer" style={{
                backgroundImage: 'linear-gradient(90deg, #ffffff 0%, #c4b5fd 25%, #ffffff 50%, #93c5fd 75%, #ffffff 100%)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text'
              }} data-id="element-195">
                    GSF
                  </motion.h1>
                </motion.div>
              </motion.div>

              {/* Impact flash on landing */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: [0, 0.8, 0],
            scale: [0.5, 2.5, 3]
          }} transition={{
            duration: 0.8,
            delay: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-196">
                <div className="w-40 h-40 md:w-64 md:h-64 rounded-full bg-purple-500/30 blur-3xl" data-id="element-197" />
              </motion.div>

              {/* Decorative line */}
              <motion.div initial={{
            scaleX: 0,
            opacity: 0
          }} animate={{
            scaleX: 1,
            opacity: 1
          }} transition={{
            duration: 1.2,
            delay: 1,
            ease: [0.22, 1, 0.36, 1]
          }} className="mx-auto h-[2px] mt-2 mb-4" data-id="element-198" />

              <motion.h2 initial={{
            opacity: 0,
            y: 20,
            letterSpacing: '0.8em'
          }} animate={{
            opacity: 0.9,
            y: 0,
            letterSpacing: '0.4em'
          }} transition={{
            duration: 1.5,
            delay: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="font-['Inter'] font-bold text-2xl md:text-4xl text-purple-100 uppercase" data-id="element-199">
                Agency
              </motion.h2>

              {/* Mission Statement */}
              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 1.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="max-w-xl mx-auto backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-6 md:p-8 text-white text-base md:text-lg leading-relaxed italic text-center" data-id="element-200">
                "Our mission is to build a team where every voice matters, every
                dream is supported, and every person is given the chance to
                shine. Through belief, encouragement, and unity, we lift each
                other higher—reaching not just for success, but for the stars."
              </motion.p>
              <ActionButtons currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-201" />
            </motion.div>
          </motion.div>}

        {currentPage === 'team' && <motion.div key="team" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-202">
            {/* Team page background */}
            <img src="https://cdn.magicpatterns.com/uploads/kk2tmHcZaXoA6VFqLNQMNa/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-203" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-204" />
            <MeetTheTeam data-id="element-205" />
            <div className="relative z-10" data-id="element-206">
              <ActionButtons currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-207" />
            </div>
          </motion.div>}

        {/* About Us page */}
        {currentPage === 'about' && <motion.div key="about" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-208">
            {/* About Us page background */}
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-209" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-210" />
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-24" data-id="element-211">
              <div className="max-w-3xl mx-auto" data-id="element-212">
                <motion.h2 initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.5
            }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 text-center mb-10" data-id="element-213">
                  About Us
                </motion.h2>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4,
              duration: 0.6
            }} className="backdrop-blur-sm bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12 space-y-6 text-white/80 text-lg leading-relaxed font-['Montserrat']" data-id="element-214">
                  <p data-id="element-215">
                    At GSF Agency, we help creators grow in a way that actually
                    feels real and sustainable.
                  </p>
                  <p data-id="element-216">
                    Whether you're posting regular content, going live, or still
                    figuring things out, we help you connect it all and find
                    your niche. Our goal is to help you build a community that
                    supports you — and eventually become sustainable doing what
                    you love.
                  </p>
                  <p data-id="element-217">
                    It doesn't cost anything to join, but you'll gain real
                    guidance from experienced creators who want to see you win.
                  </p>
                  <p data-id="element-218">
                    We're made up of all kinds of creators — from music artists
                    and DJs to life coaches and travel agents — and everyone
                    brings something different to the table.
                  </p>
                  <p className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300 font-bold text-xl text-center" data-id="element-219">
                    At the end of the day, GSF isn't just an agency… it's a
                    community.
                  </p>
                  <p className="text-white font-bold text-xl text-center mt-4" data-id="element-220">
                    We grow together, support each other, and celebrate every
                    step forward.
                  </p>
                </motion.div>
                <ActionButtons currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-221" />
              </div>
            </div>
          </motion.div>}

        {/* Why GSF page */}
        {currentPage === 'why' && <motion.div key="why" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-222">
            {/* Why GSF page background */}
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-223" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-224" />
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-24" data-id="element-225">
              <div className="max-w-3xl mx-auto" data-id="element-226">
                <motion.h2 initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.5
            }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 text-center mb-10" data-id="element-227">
                  Why GSF?
                </motion.h2>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4,
              duration: 0.6
            }} className="backdrop-blur-sm bg-white/[0.02] border border-white/5 rounded-2xl p-8 md:p-12 space-y-5 font-['Montserrat']" data-id="element-228">
                  {["You're not just joining an agency — you're joining a team that actually shows up for you", 'Real guidance from creators who are already doing this, not guessing their way through it', 'We help you find your niche, voice, income.', 'Learn how to grow your platform and turn it into real income', 'Support on both content creation and live streaming — everything connects here', 'A community that collaborates, not competes', 'music, lifestyle, coaching, travel, more', 'Built to help you become self-sustaining, not dependent'].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.5 + i * 0.1,
                duration: 0.4
              }} className="flex items-start gap-4" data-id="element-229">
                      <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-cyan-400" data-id="element-230" />
                      <p className="text-white/80 text-lg leading-relaxed" data-id="element-231">
                        {item}
                      </p>
                    </motion.div>)}
                </motion.div>
                <ActionButtons currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-232" />
              </div>
            </div>
          </motion.div>}

        {/* Contact page */}
        {currentPage === 'contact' && <motion.div key="contact" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-233">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-234" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-235" />
            <div className="relative z-10 min-h-screen px-4 pt-28 pb-24 overflow-y-auto" data-id="element-236">
              <div className="max-w-2xl mx-auto text-center" data-id="element-237">
                <motion.h2 initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.5
            }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-6" data-id="element-238">
                  Join GSF Agency
                </motion.h2>
                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4,
              duration: 0.5
            }} className="text-white/60 text-lg font-['Montserrat'] mb-10" data-id="element-239">
                  Ready to grow with a community that actually supports you? Tap
                  below to join.
                </motion.p>
                <motion.a href="https://www.tiktok.com/t/ZTkJ13tPV/" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6,
              duration: 0.5
            }} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-8 py-4 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white font-semibold text-lg font-['Montserrat'] shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_50px_rgba(168,85,247,0.6)] hover:border-purple-500/30 transition-all" data-id="element-240">
                  Join GSF Agency
                </motion.a>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8,
              duration: 0.5
            }} className="mt-12 backdrop-blur-sm bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-8 text-left font-['Montserrat']" data-id="element-241">
                  <p className="text-white/70 text-base leading-relaxed mb-4" data-id="element-242">
                    <span className="text-yellow-400 font-semibold" data-id="element-243">
                      ⚠️ Already in another agency?
                    </span>{' '}
                    You'll need to request to leave your current agency first.
                    After leaving, there's a{' '}
                    <span className="text-white font-semibold" data-id="element-244">
                      60-day waiting period
                    </span>{' '}
                    before you can apply to a new one.
                  </p>
                  <p className="text-white/80 text-base leading-relaxed" data-id="element-245">
                    However, if you're already in the process of leaving and
                    want to come over to GSF —{' '}
                    <span className="text-purple-300 font-semibold" data-id="element-246">
                      reach out to any of our agency agents
                    </span>{' '}
                    listed on the{' '}
                    <span className="text-cyan-300 font-semibold" data-id="element-247">
                      Meet the Team
                    </span>{' '}
                    page. We can start taking you under our wing immediately. We
                    don't believe in turning our backs on people.
                  </p>
                </motion.div>

                {/* 1. Contact Section */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.0,
              duration: 0.5
            }} id="contact-section" className="mt-12 backdrop-blur-sm bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-8 text-center font-['Montserrat']" data-id="element-248">
                  <p className="text-white/80 text-lg leading-relaxed mb-4" data-id="element-249">
                    If you have any questions about joining or what we offer,
                    feel free to reach one of our agents here:
                  </p>
                  <a href="mailto:gsfliveagency@gmail.com" className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all" data-id="element-250">
                    gsfliveagency@gmail.com
                  </a>
                </motion.div>
              </div>

              {/* 2. Meet the Owner */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.2,
            duration: 0.5
          }} className="mt-20 text-center max-w-4xl mx-auto" data-id="element-251">
                <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-252">
                  Meet the Owner
                </h3>
                <a href="https://www.tiktok.com/@zoeaking" target="_blank" rel="noopener noreferrer" className="inline-block transition-transform hover:scale-105" data-id="element-253">
                  <img src="https://cdn.magicpatterns.com/uploads/5J4J4wWeeLGCbuJfz3LCug/file_00000000aa0c71fb8c106d24270950d5.png" alt="StarPlayer757 - Owner" className="max-w-xs mx-auto rounded-3xl border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-254" />
                </a>
              </motion.div>

              {/* 3. Meet Your Agents */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.4,
            duration: 0.5
          }} className="mt-20 text-center max-w-6xl mx-auto" data-id="element-255">
                <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-10" data-id="element-256">
                  Meet Your Agents
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8" data-id="element-257">
                  {[{
                img: 'https://cdn.magicpatterns.com/uploads/fSqbnxnSqoF5U5YkZ7b7LY/file_0000000049d071f7b54df7f1981976fd.png',
                link: 'https://www.tiktok.com/@nfskmx26',
                name: 'BaddieDaBoss'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/qpcT4fcYS176rPdmsozKqu/file_000000008e7871f7a76604bb1ea27fa8.png',
                link: 'https://www.tiktok.com/@trippin.with.mandy',
                name: 'trippin.with.mandy'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/8n8zb1S8vSdVfh1eWwA586/file_0000000034d071fd89faaff830ddebb2.png',
                link: 'https://www.tiktok.com/@chyyyy...0110',
                name: 'j3'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/grkaBkggquQt87KznVvQ3T/file_00000000b78471f78010cbaf189ed5aa.png',
                link: 'https://www.tiktok.com/@codmshorty',
                name: 'shorty713'
              }].map((agent, idx) => <motion.a key={idx} href={agent.link} target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.6 + idx * 0.1,
                duration: 0.5
              }} className="block transition-transform hover:scale-105" data-id="element-258">
                      <img src={agent.img} alt={agent.name} className="max-w-xs w-full rounded-2xl border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-259" />
                    </motion.a>)}
                </div>
              </motion.div>

              {/* 4. Meet Your GFS/QVC Coaching Team */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 2.0,
            duration: 0.5
          }} className="mt-20 mb-10 text-center max-w-4xl mx-auto" data-id="element-260">
                <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-10" data-id="element-261">
                  Meet Your GFS/QVC Coaching Team
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mx-auto" data-id="element-262">
                  {[{
                img: 'https://cdn.magicpatterns.com/uploads/cs3mxhw7ndyQH6qEwjBfcw/file_000000002c6871fbb2bafc86da8f7c19.png',
                link: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
                name: 'ZettaMINDset'
              }, {
                img: 'https://cdn.magicpatterns.com/uploads/sMi12HB1a8MQnQZQeJ5nBM/file_00000000e00071f78578a71f0cdf321a.png',
                link: 'https://www.tiktok.com/@trippin.with.mandy',
                name: 'trippin.with.mandy'
              }].map((coach, idx) => <motion.a key={idx} href={coach.link} target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 2.2 + idx * 0.15,
                duration: 0.5
              }} className="block transition-transform hover:scale-105" data-id="element-263">
                      <img src={coach.img} alt={coach.name} className="max-w-xs w-full mx-auto rounded-2xl border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-264" />
                    </motion.a>)}
                </div>
              </motion.div>

              {/* Contact Button at bottom */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 2.5,
            duration: 0.5
          }} className="mt-16 mb-10 text-center" data-id="element-265">
                <motion.a href="mailto:gsfliveagency@gmail.com" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-lg font-['Montserrat'] shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_50px_rgba(168,85,247,0.6)] transition-all" data-id="element-266">
                  Contact Us
                </motion.a>
              </motion.div>
            </div>
          </motion.div>}

        {currentPage === 'services' && <motion.div key="services" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-267">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-268" />
            <div className="fixed inset-0 bg-black/50 pointer-events-none z-0" data-id="element-269" />
            <div className="relative z-10 min-h-screen px-4 pt-28 pb-24" data-id="element-270">
              <div className="max-w-3xl mx-auto" data-id="element-271">
                <motion.h2 initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.5
            }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 text-center mb-4" data-id="element-272">
                  💫 What We Help You With
                </motion.h2>
                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4,
              duration: 0.5
            }} className="text-center text-white/50 text-base font-['Montserrat'] mb-12" data-id="element-273">
                  Everything you need to grow as a creator — all in one place.
                </motion.p>

                <div className="space-y-5" data-id="element-274">
                  {[{
                emoji: '🎯',
                title: 'Niche & Content Direction',
                desc: 'help you post, creator, and stand out.'
              }, {
                emoji: '📱',
                title: 'Live Streaming Coaching',
                desc: 'Learn live with confidence, engage, grow.'
              }, {
                emoji: '🚀',
                title: 'Growth Strategy',
                desc: 'build followers, reach, and stay consistent.'
              }, {
                emoji: '💰',
                title: 'Monetization Guidance',
                desc: 'content into income (gifts, deals, etc)'
              }, {
                emoji: '🧠',
                title: 'Mindset & Confidence Support',
                desc: "Because let's be real—this is the part that stops most people. We help you push through it."
              }, {
                emoji: '🤝',
                title: 'Community Support',
                desc: "You're not doing this alone. You'll be surrounded by creators growing together."
              }, {
                emoji: '📊',
                title: 'Profile & Content Reviews',
                desc: "We'll look at your page and give real feedback so you know what to fix and improve."
              }, {
                emoji: '🎥',
                title: 'Content Ideas & Planning',
                desc: 'Never wonder "what do I post?" again—we help you stay full of ideas.'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -30
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.5 + i * 0.1,
                duration: 0.4
              }} className="p-6 md:p-6 shadow-[0 0 15px rgba(168,85,247,0.1)]" data-id="element-275">
                      <span className="text-3xl md:text-4xl flex-shrink-0 mt-1" data-id="element-276">
                        {item.emoji}
                      </span>
                      <div data-id="element-277">
                        <h3 className="font-['Montserrat'] font-bold text-lg md:text-xl text-white mb-1" data-id="element-278">
                          {item.title}
                        </h3>
                        <p className="font-['Montserrat'] text-white/70 text-base leading-relaxed" data-id="element-279">
                          {item.desc}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
                <ActionButtons currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-280" />
              </div>
            </div>
          </motion.div>}

        {currentPage === 'agent-login' && <motion.div key="agent-login" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-281">
            <LoginPage title="Agent Login" type="agent" onLogin={displayName => {
          setAgentDisplayName(displayName);
          setCurrentPage('portal-hub');
        }} data-id="element-282" />
          </motion.div>}

        {currentPage === 'creator-login' && <motion.div key="creator-login" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-283">
            <LoginPage title="Creator Login" type="creator" onLogin={displayName => {
          setCreatorDisplayName(displayName);
          setCurrentPage('creator-hub');
        }} data-id="element-284" />
          </motion.div>}

        {/* ── Agent Portals ── */}
        {currentPage === 'portal-hub' && <motion.div key="portal-hub" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-285">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-286" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-287" />
            <PortalHub title="Agent Portal" subtitle="Select a chat board to enter" onNavigate={board => setCurrentPage(board === 'gsf' ? 'portal' : 'portal-qvc')} onSignOut={() => {
          signOut(auth);
          setAgentDisplayName('');
          setCurrentPage('home');
        }} data-id="element-288" />
          </motion.div>}

        {currentPage === 'portal' && <motion.div key="portal" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-289">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-290" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-291" />
            <PortalPage user={user} displayName={agentDisplayName} title="GSF Live Agent Chat" subtitle="Main agency updates and team chat." collectionName="posts" onBack={() => setCurrentPage('portal-hub')} onSignOut={() => {
          signOut(auth);
          setAgentDisplayName('');
          setCurrentPage('home');
        }} data-id="element-292" />
          </motion.div>}

        {currentPage === 'portal-qvc' && <motion.div key="portal-qvc" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-293">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-294" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-295" />
            <PortalPage user={user} displayName={agentDisplayName} title="GSF/QVC Live Agent Chat" subtitle="QVC selling side updates and strategies." collectionName="qvc_posts" onBack={() => setCurrentPage('portal-hub')} onSignOut={() => {
          signOut(auth);
          setAgentDisplayName('');
          setCurrentPage('home');
        }} data-id="element-296" />
          </motion.div>}

        {/* ── Creator Portals ── */}
        {currentPage === 'creator-hub' && <motion.div key="creator-hub" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-297">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-298" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-299" />
            <PortalHub title="Creator Dashboard" subtitle="Select a chat board to enter" onNavigate={board => setCurrentPage(board === 'gsf' ? 'creator-portal' : 'creator-portal-qvc')} onSignOut={() => {
          signOut(creatorAuth);
          setCreatorDisplayName('');
          setCurrentPage('home');
        }} data-id="element-300" />
          </motion.div>}

        {currentPage === 'creator-portal' && <motion.div key="creator-portal" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-301">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-302" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-303" />
            <CreatorPortalPage user={creatorUser} displayName={creatorDisplayName} title="GSF Live Creator Chat" subtitle="Main creator updates and community chat." collectionName="posts" onBack={() => setCurrentPage('creator-hub')} onSignOut={() => {
          signOut(creatorAuth);
          setCreatorDisplayName('');
          setCurrentPage('home');
        }} data-id="element-304" />
          </motion.div>}

        {currentPage === 'creator-portal-qvc' && <motion.div key="creator-portal-qvc" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4
      }} className="relative" data-id="element-305">
            <img src="https://cdn.magicpatterns.com/uploads/2SWc2LaHUNoKRAGe1SRRKA/file_0000000067cc722fa5b5577ab39eaf21.png" alt="Galaxy lounge background" className="fixed inset-0 w-full h-full object-cover z-0" data-id="element-306" />
            <div className="fixed inset-0 bg-black/40 pointer-events-none z-0" data-id="element-307" />
            <CreatorPortalPage user={creatorUser} displayName={creatorDisplayName} title="GSF/QVC Live Creator Chat" subtitle="QVC selling side updates and strategies." collectionName="qvc_posts" onBack={() => setCurrentPage('creator-hub')} onSignOut={() => {
          signOut(creatorAuth);
          setCreatorDisplayName('');
          setCurrentPage('home');
        }} data-id="element-308" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}